import { isSameMonth } from 'date-fns';
import { useMemo } from 'react';
import { ReactComponent as CalendarIcon } from '../../icons/calendar.svg';
import Collapse from '../../components/Collapse/Collapse';
import { getUTCDate } from '../../core/helpers';
import SellerCalendar from './SellerCalendar';
import './SellerCalendarCollapse.scss';
import { Report } from '../../core/types';
import { normalizeDateFE } from '../../core/normalization';
import { useTranslation } from 'react-i18next';
import Button from '../../components/Button';

interface Props {
  reports: Report[];
}

export default function SellerCalendarCollapse({ reports }: Props) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const currentReports = reports.filter((r) => {
      const curDate = getUTCDate();
      const curYear = curDate.getFullYear();
      const curMonth = curDate.getMonth();

      const date = normalizeDateFE(r.deadline_date);
      const reportMonth = date.getMonth();
      const reportYear = date.getFullYear();

      return (
        reportMonth >= curMonth &&
        reportMonth <= curMonth + 2 &&
        reportYear === curYear
      );
    });

    const months = currentReports
      .map((r) => {
        const date = normalizeDateFE(r.deadline_date);
        const reportMonth = date.getMonth();
        return reportMonth;
      })
      .filter((item, i, self) => self.indexOf(item) === i)
      .sort((a, b) => a - b);

    const items = currentReports
      .map((r) => r.deadline_date)
      .filter((item, i, self) => self.indexOf(item) === i)
      .map((d) => {
        return {
          date: normalizeDateFE(d),
          countries: currentReports
            .filter((r) => r.deadline_date === d)
            .map((r) => {
              if (r.type === 'oss') return 'EU';
              return r.country.short_name;
            })
            .filter((item, i, self) => self.indexOf(item) === i),
        };
      });

    return months.map((m) => {
      const date = getUTCDate();
      date.setDate(1);
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);
      date.setMonth(m);

      return {
        month: date,
        items: items
          .filter((item) => isSameMonth(date, item.date))
          .sort((a, b) => a.date.getTime() - b.date.getTime()),
      };
    });
  }, [reports]);

  const today = getUTCDate();
  const curCalendar = data.find((c) => isSameMonth(today, c.month));

  if (!curCalendar) return null;

  return (
    <div className='Paper w-100 mb-3 pt-2 pb-3'>
      <Collapse
        triggerRenderer={(isOpened, toggleCollapse) => (
          <div className='px-3'>
            <div>
              <div className='d-flex align-items-center mb-2'>
                <CalendarIcon className='me-2' />
                <div className='Text fw-bold' data-testid='vat-calendar-title'>
                  {t('VAT calendar')}
                </div>
              </div>
              <div
                className='mb-2'
                dangerouslySetInnerHTML={{
                  __html: `<span>${t(
                    'We recommend to transfer VAT payment at least *hours*'
                  ).replace(
                    '*hours*',
                    `<span class='fw-600'>${t('48h before due date.')}</span>`
                  )}</span>`,
                }}
              />

              {!isOpened && (
                <SellerCalendar
                  data={curCalendar}
                  data-testid='current'
                ></SellerCalendar>
              )}
            </div>

            {!isOpened && data.length > 1 && (
              <Button
                styling='naked-green'
                className='fw-600'
                onClick={toggleCollapse}
              >
                {data.length < 3
                  ? t('Show next month')
                  : t('Show *n* next months').replace(
                      '*n*',
                      String(data.length - 1)
                    )}
              </Button>
            )}
          </div>
        )}
        contentRenderer={(ref, toggleCollpase) => (
          <div ref={ref} className='px-3'>
            <div>
              {data.map((d, i) => (
                <SellerCalendar
                  data={d}
                  className='SellerCalendarCollapse__calendar mb-2'
                  key={d.month.getMonth()}
                />
              ))}
            </div>
            <Button
              styling='naked-green'
              className='mt-n2 fw-600'
              onClick={toggleCollpase}
            >
              {t('Show current month')}
            </Button>
          </div>
        )}
      />
    </div>
  );
}
